import React from 'react';
import styled from 'styled-components';
import { Border, Color, Grid, Shadow, Type } from '../../../app/StyleGuide';
import { RoundedPrimaryBadge } from '../../../components/Badge';
import windowService from '../../../services/windowService';
import { getRecentlyPaired } from '../../pairing';
import { AgeCategoryAccess } from '../api/getCurriculumAccess';
import { AgeCategory, BrandCode } from '../models/curriculum';

export const BibleStudyDescription = styled.p`
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.light};
  text-align: center;
  color: ${Type.Color.white};
  white-space: pre-line;
  margin: 0;
  padding: ${Grid._5} 0;
  text-shadow: ${Type.Shadow.light};

  & + ul {
    /* Ensures the first age group item aligns for all cards whether the list
       begins with subscribed or unsubscribed age groups */
    margin-top: 26px;
  }
`;

export const BibleStudyCard = styled.div<{ backgroundColor: string; theme: 'light' | 'dark' }>`
  display: flex;
  flex-direction: column;
  border-radius: ${Border.radius};
  border-color: ${Color.Gray._30};
  box-shadow: ${Shadow.regular};
  background: ${props => props.backgroundColor || 'white'};
  padding: ${Grid._5} ${Grid._4} ${Grid._3};

  ${({ theme }) =>
    theme === 'dark' &&
    `
    ${AgeGroupSectionName} {
      color: ${Type.Color.dark};
    }

    ${BibleStudyDescription} {
      color: ${Type.Color.dark};
      text-shadow: none;
    }
  `}
`;

export type AgeCategoryEntry = [AgeCategory, AgeCategoryAccess];

export type BibleStudyAgeGroupsProps = {
  brandCode: BrandCode;
  brandName: string;
  subscribedAgeGroups: AgeCategoryEntry[];
  unsubscribedAgeGroups: AgeCategoryEntry[];
  recentlyPaired?: ReturnType<typeof getRecentlyPaired>;
};

export const AgeGroups = styled.ul`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  padding: 0;
  margin: 0;
  list-style: none;
`;

export const AgeGroup = styled.li`
  margin-bottom: ${Grid._4};
`;

export const AgeGroupSectionName = styled.p`
  font-size: ${Type.Scale._1};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.white};
  margin-bottom: ${Grid._3};
`;

export const ActionRowButton = styled.a`
  height: 57px;
  text-decoration: none;
  cursor: pointer;
  border-radius: ${Border.radius};
  color: ${Type.Color.dark} !important;
  padding: ${Grid._4};
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.medium};
  background: rgba(255, 255, 255, 0.7);
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.125s linear;
  i {
    color: ${Type.Color.dark};
  }
  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }
`;

export const InlineBadge = styled.span`
  color: ${Type.Color.dark};
  font-size: ${Type.Scale._05};
  text-transform: uppercase;
  border: 1px solid ${Type.Color.dark};
  border-radius: 25px;
  padding: ${Grid._2} ${Grid._3};
`;

export const InverseInlineBadge = styled(InlineBadge)`
  color: ${Type.Color.white};
  background: ${props => props.color || Type.Color.dark};
  border: none;
`;

type SubscribedAgeGroupsSectionProps = {
  sectionName: string;
  brandCode: string;
  entryRoute: 'overview' | 'people';
  subscribedAgeGroups: AgeCategoryEntry[];
  recentlyPaired?: ReturnType<typeof getRecentlyPaired>;
};

export function SubscribedAgeGroupsSection({
  sectionName,
  brandCode,
  entryRoute,
  subscribedAgeGroups,
  recentlyPaired,
}: SubscribedAgeGroupsSectionProps) {
  if (!subscribedAgeGroups.length) return null;

  const isRecentlyPaired = (ageCategory: AgeCategory) =>
    recentlyPaired?.some(
      ({ subtitle: pairedAgeCategory, brandCode: pairedBrandCode }) =>
        pairedBrandCode === brandCode && pairedAgeCategory === ageCategory
    );

  return (
    <>
      <AgeGroupSectionName>{sectionName}</AgeGroupSectionName>
      <AgeGroups>
        {subscribedAgeGroups.map(([name, accessInfo]) => (
          <AgeGroup key={accessInfo.id}>
            <AgeGroupButton
              data-qa-hook={`${brandCode}-${name}`}
              aria-label={`navigate to ${brandCode} ${name} curriculum`}
              name={name}
              onClick={() =>
                windowService.redirectTo(`#/manage-bible-study/${entryRoute}/${brandCode}/${name}/${accessInfo.id}`)
              }
              badgeComponent={
                isRecentlyPaired(name) ? (
                  <RoundedPrimaryBadge>New</RoundedPrimaryBadge>
                ) : (
                  <i className="fas fa-chevron-right" />
                )
              }
            />
          </AgeGroup>
        ))}
      </AgeGroups>
    </>
  );
}

export const AgeGroupButton = ({
  name,
  onClick,
  badgeComponent,
  'data-qa-hook': dataQaHook,
  'aria-label': ariaLabel,
}: {
  name: string;
  onClick: () => void;
  badgeComponent: React.ReactNode;
  'data-qa-hook': string;
  'aria-label': string;
}) => (
  <ActionRowButton data-qa-hook={dataQaHook} aria-label={ariaLabel} onClick={onClick}>
    {name}
    {badgeComponent}
  </ActionRowButton>
);
